import { useState, useMemo, useRef, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Slider } from '@/components/ui/slider';
import { 
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle 
} from '@/components/ui/alert-dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Download, Table as TableIcon, Loader2, Truck, ArrowLeft, ArrowUpDown, 
  BarChart3, PieChart as PieChartIcon, AreaChart as AreaChartIcon, 
  RotateCcw, TrendingUp, Activity, Target, LineChart as LineChartIcon,
  Euro, MapPin, Zap, Building2, ChevronDown, ChevronLeft, ChevronRight,
  Settings2, Filter, FileSpreadsheet, Users, Presentation, GripVertical
} from 'lucide-react';
import { DndContext, closestCenter, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, horizontalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import JSZip from 'jszip';
import { useToast } from '@/hooks/use-toast';
import html2canvas from 'html2canvas';
import * as XLSX from 'xlsx';
import {
  KAVEL_FASEN_MET_REST,
  faseLabel,
  getStageGroup,
  kavelZicht,
} from '@/lib/chargingStages';
import gvccLogo from '@/assets/gvcc-logo.png';
import { cn } from '@/lib/utils';
import { Switch } from '@/components/ui/switch';
import { LocationSlideTemplate } from '@/components/charging/LocationSlideTemplate';
import {
  BarChart, Bar, PieChart, Pie, Cell,
  AreaChart, Area, LineChart, Line,
  RadialBarChart, RadialBar,
  XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend
} from 'recharts';

interface ColumnOption {
  id: string;
  label: string;
  default: boolean;
}

const COLUMNS: ColumnOption[] = [
  { id: 'locode', label: 'LOCODE', default: true },
  { id: 'name', label: 'Locatie', default: true },
  { id: 'city', label: 'Stad', default: false },
  { id: 'stage', label: 'Status', default: false },
  { id: 'ownership_type', label: 'Eigendomstype', default: false },
  { id: 'surface_area_m2', label: 'Oppervlakte (m²)', default: false },
  { id: 'contracted_power_kw', label: 'Vermogen (kW)', default: false },
  { id: 'total_investment', label: 'Investering (€)', default: false },
  { id: 'equity_percentage', label: 'Equity (%)', default: false },
  { id: 'equity_amount', label: 'Equity (€)', default: false },
  { id: 'financed_percentage', label: 'Gefinancierd (%)', default: false },
  { id: 'financed_amount', label: 'Gefinancierd (€)', default: false },
  { id: 'subsidy_amount', label: 'Subsidie (€)', default: false },
  { id: 'cost_land_purchase', label: 'Aankoop grond (€)', default: false },
  { id: 'cost_land_development', label: 'Aanleg grond (€)', default: false },
  { id: 'cost_engineering', label: 'Engineering (€)', default: false },
  { id: 'cost_charging_infra', label: 'Laadinfra (€)', default: false },
  { id: 'development_size_chargers', label: 'Laadpalen', default: false },
  { id: 'target_quarter', label: 'Timing', default: false },
  { id: 'interested_parties', label: 'Geïnteresseerde partijen', default: true },
  { id: 'total_trucks', label: 'Trucks', default: true },
  { id: 'notes', label: 'Notities', default: false }, // Notes at the end
];

// Default column order (notes at the end)
const DEFAULT_COLUMN_ORDER = COLUMNS.filter(c => c.default).map(c => c.id);

const THEMES = [
  { id: 'greenvision', label: 'Greenvision', primary: '#22c55e', secondary: '#f0fdf4' },
  { id: 'greenvision-charging', label: 'Greenvision Charging', primary: '#080c55', secondary: 'rgba(0, 164, 212, 0.15)' },
  { id: 'neutral', label: 'Neutraal', primary: '#64748b', secondary: '#f8fafc' },
  { id: 'dark', label: 'Donker', primary: '#1e293b', secondary: '#334155' },
];

// De vier fasen plus de restgroep, uit de enige lijst die ze kent (T2.5).
const STAGES = KAVEL_FASEN_MET_REST.map((f) => ({ id: f.id, label: f.label, color: f.color }));

/**
 * Staan slapende Kavels standaard in deze tabel?
 *
 * Eén constante, want dit is een keuze van Stan en geen eigenschap van de
 * code: de Gauw-tabel is het overzicht dat naar buiten gaat, en of daar de
 * hele portefeuille of alleen het lopende werk in hoort is een inhoudelijke
 * vraag. `false` volgt de fase-schermen; op `true` zetten is één regel.
 */
const SLAPEND_STANDAARD_ZICHTBAAR = false;

type ChartType = 'bar' | 'pie' | 'stacked' | 'area' | 'line' | 'donut' | 'radial';
type ChartMetric = 'count' | 'investment' | 'trucks' | 'equity' | 'power' | 'surface' | 'chargers' | 'financed';
type ChartDataView = 'stage' | 'city' | 'ownership' | 'costs' | 'comparison';

const CHART_TYPES = [
  { id: 'bar', label: 'Staaf', icon: BarChart3 },
  { id: 'pie', label: 'Taart', icon: PieChartIcon },
  { id: 'donut', label: 'Donut', icon: Target },
  { id: 'stacked', label: 'Gestapeld', icon: BarChart3 },
  { id: 'area', label: 'Vlak', icon: AreaChartIcon },
  { id: 'line', label: 'Lijn', icon: LineChartIcon },
  { id: 'radial', label: 'Radiaal', icon: Activity },
];

const METRICS = [
  { id: 'count', label: 'Aantal Locaties', icon: MapPin },
  { id: 'investment', label: 'Investering', icon: Euro },
  { id: 'equity', label: 'Equity', icon: TrendingUp },
  { id: 'financed', label: 'Gefinancierd', icon: Building2 },
  { id: 'trucks', label: 'Trucks', icon: Truck },
  { id: 'power', label: 'Vermogen (kW)', icon: Zap },
  { id: 'surface', label: 'Oppervlakte (m²)', icon: MapPin },
  { id: 'chargers', label: 'Laadpalen', icon: Zap },
];

const CHART_COLORS = ['#080c55', '#00a4d4', '#0091ff', '#ff9100', '#28a745', '#e53935', '#9c27b0', '#ff5722'];

// Storage URL pattern - same logic as Edge Function
const STORAGE_BASE = 'https://fxlorumfwzrdwiftbalo.supabase.co/storage/v1/object/public/cached-favicons';

// Get predictable Storage URL for a domain (no network request needed)
function getStorageUrl(domain: string): string {
  const fileName = `${domain.replace(/[^a-zA-Z0-9]/g, '_')}.png`;
  return `${STORAGE_BASE}/${fileName}`;
}

// Track domains that failed to load (to avoid retrying)
const failedDomains = new Set<string>();

// Track domains currently being cached (to avoid duplicate requests)
const cachingInProgress = new Map<string, Promise<string | null>>();

// Helper to check if URL is a Google favicon URL
function isGoogleFaviconUrl(url?: string | null): boolean {
  return !!url && url.includes('google.com/s2/favicons');
}

// Extract domain from Google favicon URL
function extractDomainFromGoogleUrl(url: string): string | null {
  try {
    const urlObj = new URL(url);
    return urlObj.searchParams.get('domain');
  } catch {
    return null;
  }
}

// Extract domain from any URL
function extractDomain(url: string): string {
  return url.replace(/^https?:\/\//, '').split('/')[0];
}

// Get domain for an organization (from website or google favicon URL)
function getOrgDomain(org: { website?: string | null; favicon_url?: string | null }): string | null {
  if (org.website) {
    return extractDomain(org.website);
  }
  if (isGoogleFaviconUrl(org.favicon_url)) {
    return extractDomainFromGoogleUrl(org.favicon_url!);
  }
  return null;
}

// Lazy cache favicon - only called when image fails to load from Storage
async function cacheFaviconLazy(domain: string): Promise<string | null> {
  // Already failed before
  if (failedDomains.has(domain)) {
    return null;
  }
  
  // Already caching
  if (cachingInProgress.has(domain)) {
    return cachingInProgress.get(domain)!;
  }
  
  const cachePromise = (async () => {
    try {
      console.log('[Favicon] Lazy caching:', domain);
      const response = await supabase.functions.invoke('cache-favicon', {
        body: { domain }
      });
      
      if (response.error || !response.data?.url) {
        console.warn('[Favicon] Cache failed:', { domain, error: response.error });
        failedDomains.add(domain);
        return null;
      }
      
      console.log('[Favicon] Cached:', { domain, url: response.data.url });
      return response.data.url;
    } catch (error) {
      console.error('[Favicon] Error:', { domain, error });
      failedDomains.add(domain);
      return null;
    } finally {
      cachingInProgress.delete(domain);
    }
  })();
  
  cachingInProgress.set(domain, cachePromise);
  return cachePromise;
}

// Sortable header component for drag-and-drop column reordering
interface SortableHeaderProps {
  column: ColumnOption;
  headerStyle: 'bold' | 'colored' | 'underlined';
  borderStyle: 'none' | 'light' | 'full';
  currentTheme: { primary: string; secondary: string };
  sortColumn: string | null;
  sortDirection: 'asc' | 'desc';
  onSort: (colId: string) => void;
}

function SortableHeader({ 
  column, 
  headerStyle, 
  borderStyle, 
  currentTheme, 
  sortColumn, 
  sortDirection, 
  onSort 
}: SortableHeaderProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ 
    id: column.id 
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    borderRight: borderStyle !== 'none' 
      ? `1px solid ${headerStyle === 'colored' ? 'rgba(255,255,255,0.3)' : currentTheme.primary}30` 
      : 'none',
  };

  return (
    <th
      ref={setNodeRef}
      style={style}
      className={`p-2 text-left select-none ${headerStyle === 'bold' ? 'font-bold' : 'font-semibold'}`}
    >
      <div className="flex items-center gap-1">
        {/* Drag handle */}
        <div 
          {...attributes} 
          {...listeners} 
          className="cursor-grab hover:opacity-70 touch-none"
          title="Sleep om te herschikken"
        >
          <GripVertical className="h-3 w-3 opacity-50" />
        </div>
        {/* Clickable label for sorting */}
        <div 
          className="flex items-center gap-1 cursor-pointer hover:opacity-80"
          onClick={() => column.id !== 'interested_parties' && onSort(column.id)}
        >
          {column.label}
          {sortColumn === column.id && (
            <span className="text-xs">{sortDirection === 'asc' ? '↑' : '↓'}</span>
          )}
          {column.id !== 'interested_parties' && sortColumn !== column.id && (
            <ArrowUpDown className="h-3 w-3 opacity-30" />
          )}
        </div>
      </div>
    </th>
  );
}

export default function ChargingGauwTabel() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const tableRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<HTMLDivElement>(null);
  const [exportingSlides, setExportingSlides] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  const [selectedColumns, setSelectedColumns] = useState<Set<string>>(
    new Set(COLUMNS.filter(c => c.default).map(c => c.id))
  );
  // Column order for drag-and-drop reordering
  const [columnOrder, setColumnOrder] = useState<string[]>(DEFAULT_COLUMN_ORDER);
  
  const [theme, setTheme] = useState('greenvision-charging');
  const [headerStyle, setHeaderStyle] = useState<'bold' | 'colored' | 'underlined'>('colored');
  const [alternatingRows, setAlternatingRows] = useState(true);
  const [borderStyle, setBorderStyle] = useState<'none' | 'light' | 'full'>('light');
  const [fontSize, setFontSize] = useState<'small' | 'medium' | 'large'>('medium');
  
  // Drag-and-drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );
  
  // Location filtering (like Export/Presentatie)
  const [visibleLocationIds, setVisibleLocationIds] = useState<Set<string>>(new Set());
  const [expandedStages, setExpandedStages] = useState<Set<string>>(new Set(['acquisitie']));
  const [filtersPanelOpen, setFiltersPanelOpen] = useState(true);
  const [toonSlapend, setToonSlapend] = useState(SLAPEND_STANDAARD_ZICHTBAAR);
  
  // Sorting
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  
  // View mode and chart settings
const [viewMode, setViewMode] = useState<'table' | 'charts'>('table');
  const [chartType, setChartType] = useState<ChartType>('bar');
  const [chartMetric, setChartMetric] = useState<ChartMetric>('count');
  const [chartDataView, setChartDataView] = useState<ChartDataView>('stage');
  const [showTrucksPerParty, setShowTrucksPerParty] = useState(true);
  const [notesMaxLength, setNotesMaxLength] = useState(50);
  
  // Missing drawings alert for bulk export
  const [showBulkMissingDrawingAlert, setShowBulkMissingDrawingAlert] = useState(false);
  const [missingDrawingLocations, setMissingDrawingLocations] = useState<any[]>([]);
  const [pendingBulkExport, setPendingBulkExport] = useState(false);

  // Fetch locations with linked opportunities and cache favicons
  const { data: tableData = [], isLoading } = useQuery({
    queryKey: ['gauw-tabel-data'],
    queryFn: async () => {
      // Fetch locations
      const { data: locations, error: locError } = await supabase
        .from('charging_locations')
        .select('*')
        .is('deleted_at', null)
        .order('locode');
      
      if (locError) throw locError;

      // Fetch opportunity links with organization info
      const { data: oppLinks, error: oppError } = await supabase
        .from('opportunity_locations')
        .select(`
          location_id,
          trucks_per_day,
          opportunity:charging_opportunities(
            name,
            org_id,
            organization:organizations(id, name, logo_url, favicon_url, website)
          )
        `);
      
      if (oppError) throw oppError;

      // Group opportunity data by location - use direct Storage URLs (no Edge Function calls!)
      const locationOppMap = new Map<string, Array<{ orgName: string; orgLogo: string | null; domain: string | null; trucks: number }>>();
      
      oppLinks?.forEach((link: any) => {
        const org = link.opportunity?.organization;
        if (!org) return;
        
        const existing = locationOppMap.get(link.location_id) || [];
        
        // Priority: logo_url > direct Storage URL (predictable, no network call)
        let orgLogo: string | null = null;
        let domain: string | null = null;
        
        if (org.logo_url) {
          orgLogo = org.logo_url;
        } else {
          domain = getOrgDomain(org);
          if (domain) {
            // Use predictable Storage URL - no Edge Function call needed!
            orgLogo = getStorageUrl(domain);
          }
        }
        
        existing.push({
          orgName: org.name,
          orgLogo,
          domain, // Store domain for lazy caching on error
          trucks: link.trucks_per_day || 0,
        });
        locationOppMap.set(link.location_id, existing);
      });

      console.log('[Tabel] Data loaded - using direct Storage URLs (no pre-caching)');

      // Combine data
      return (locations || []).map((loc: any) => ({
        ...loc,
        interested_parties: locationOppMap.get(loc.id) || [],
        total_trucks: (locationOppMap.get(loc.id) || []).reduce((sum, p) => sum + p.trucks, 0),
      }));
    },
  });

  // Initialize visible locations when data loads
  //
  // T2.5 / scepticus-ronde — deze useEffect trok twee groepen stilzwijgend af
  // (verloren, en sinds T2.5 ook slapend) zonder ergens te zeggen hoeveel.
  // Twee stille aftrekkingen op het overzicht dat naar buiten gaat. De
  // schakelaar en de tellingregel hieronder maken ze allebei zichtbaar.
  useEffect(() => {
    if (tableData.length > 0 && visibleLocationIds.size === 0) {
      const defaultVisible = tableData
        .filter((loc: any) => getStageGroup(loc.stage) !== 'verloren')
        .filter((loc: any) => (SLAPEND_STANDAARD_ZICHTBAAR ? true : kavelZicht(loc) !== 'slapend'))
        .map((loc: any) => loc.id);
      setVisibleLocationIds(new Set(defaultVisible));
      setToonSlapend(SLAPEND_STANDAARD_ZICHTBAAR);
    }
  }, [tableData, visibleLocationIds.size]);

  /**
   * Zet slapende Kavels in één keer aan of uit in de tabel. De losse vinkjes
   * in het filterpaneel blijven werken; dit is de snelkoppeling ernaast.
   */
  const zetSlapendZichtbaar = (aan: boolean) => {
    setToonSlapend(aan);
    setVisibleLocationIds((huidig) => {
      const nieuw = new Set(huidig);
      for (const loc of tableData as any[]) {
        if (kavelZicht(loc) !== 'slapend') continue;
        if (aan) nieuw.add(loc.id);
        else nieuw.delete(loc.id);
      }
      return nieuw;
    });
  };

  // De telling naast de schakelaar, over dezelfde set die de tabel toont.
  const slapendTelling = useMemo(() => {
    const slapend = (tableData as any[]).filter((l) => kavelZicht(l) === 'slapend');
    const zichtbaarSlapend = slapend.filter((l) => visibleLocationIds.has(l.id)).length;
    return {
      totaal: tableData.length,
      zichtbaar: visibleLocationIds.size,
      slapend: slapend.length,
      zichtbaarSlapend,
    };
  }, [tableData, visibleLocationIds]);

  // Group locations by stage
  const locationsByStage = useMemo(() => {
    // Alle vijf de groepen, ook verloren en de restgroep. Vóór T2.5 stonden
    // hier alleen de eerste drie en viel al het andere door de `if` op de
    // grond: die Kavels hadden in dit paneel geen regel, dus geen vinkje, dus
    // geen enkele manier om ze in de tabel te krijgen.
    const groups: Record<string, any[]> = Object.fromEntries(
      STAGES.map((s) => [s.id, [] as any[]]),
    );
    tableData.forEach((loc: any) => {
      const group = getStageGroup(loc.stage);
      (groups[group] ?? groups.onbekend).push(loc);
    });
    return groups;
  }, [tableData]);

  const currentTheme = THEMES.find(t => t.id === theme) || THEMES[0];
  
  const fontSizeClass = fontSize === 'small' ? 'text-xs' : fontSize === 'large' ? 'text-base' : 'text-sm';
  
  // Filter by visible location IDs
  const filteredTableData = useMemo(() => {
    return tableData.filter((row: any) => visibleLocationIds.has(row.id));
  }, [tableData, visibleLocationIds]);
  
  // Sort data
  const sortedTableData = useMemo(() => {
    if (!sortColumn) return filteredTableData;
    
    return [...filteredTableData].sort((a, b) => {
      let aVal: any = a[sortColumn];
      let bVal: any = b[sortColumn];
      
      // Handle calculated fields
      if (sortColumn === 'total_trucks') {
        aVal = a.total_trucks || 0;
        bVal = b.total_trucks || 0;
      } else if (sortColumn === 'equity_amount') {
        aVal = a.total_investment && a.equity_percentage ? a.total_investment * (a.equity_percentage / 100) : 0;
        bVal = b.total_investment && b.equity_percentage ? b.total_investment * (b.equity_percentage / 100) : 0;
      } else if (sortColumn === 'financed_amount') {
        aVal = a.total_investment && a.equity_percentage != null ? a.total_investment * (1 - a.equity_percentage / 100) : 0;
        bVal = b.total_investment && b.equity_percentage != null ? b.total_investment * (1 - b.equity_percentage / 100) : 0;
      } else if (sortColumn === 'financed_percentage') {
        aVal = a.equity_percentage != null ? 100 - a.equity_percentage : 0;
        bVal = b.equity_percentage != null ? 100 - b.equity_percentage : 0;
      } else if (sortColumn === 'stage') {
        aVal = getStageGroup(a.stage);
        bVal = getStageGroup(b.stage);
      }
      
      // Numeric comparison
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }
      
      // String comparison
      const aStr = String(aVal || '').toLowerCase();
      const bStr = String(bVal || '').toLowerCase();
      return sortDirection === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
    });
  }, [filteredTableData, sortColumn, sortDirection]);
  
  // Totals
  const totals = useMemo(() => {
    const investment = sortedTableData.reduce((sum, loc) => sum + (loc.total_investment || 0), 0);
    const equity = sortedTableData.reduce((sum, loc) => {
      if (loc.total_investment && loc.equity_percentage) {
        return sum + loc.total_investment * (loc.equity_percentage / 100);
      }
      return sum;
    }, 0);
    const financed = investment - equity;
    const surface = sortedTableData.reduce((sum, loc) => sum + (loc.surface_area_m2 || 0), 0);
    
    // Cost totals
    const landCost = sortedTableData.reduce((sum, loc) => sum + (loc.cost_land_purchase || 0), 0);
    const devCost = sortedTableData.reduce((sum, loc) => sum + (loc.cost_land_development || 0), 0);
    const engCost = sortedTableData.reduce((sum, loc) => sum + ((loc as any).cost_engineering || 0), 0);
    const infraCost = sortedTableData.reduce((sum, loc) => sum + (loc.cost_charging_infra || 0), 0);

    const subsidy = sortedTableData.reduce((sum, loc) => sum + (loc.subsidy_amount || 0), 0);

    return {
      trucks: sortedTableData.reduce((sum, loc) => sum + (loc.total_trucks || 0), 0),
      investment,
      equity,
      financed,
      surface,
      power: sortedTableData.reduce((sum, loc) => sum + (loc.contracted_power_kw || 0), 0),
      chargers: sortedTableData.reduce((sum, loc) => sum + (loc.development_size_chargers || 0), 0),
      equityPercentage: investment > 0 ? (equity / investment) * 100 : 0,
      financedPercentage: investment > 0 ? (financed / investment) * 100 : 0,
      landCost,
      devCost,
      engCost,
      infraCost,
      subsidy,
    };
  }, [sortedTableData]);
  
  // Chart data calculations
  const chartData = useMemo(() => {
    // Over de fasen die daadwerkelijk zichtbare rijen hebben: zo telt de
    // grafiek op tot hetzelfde als de tabel eronder, ook als iemand Verloren
    // aanvinkt (T2.5).
    return STAGES.filter(s =>
      sortedTableData.some(r => getStageGroup(r.stage) === s.id),
    ).map(stage => {
      const stageData = sortedTableData.filter(r => getStageGroup(r.stage) === stage.id);
      const stageInvestment = stageData.reduce((s, r) => s + (r.total_investment || 0), 0);
      const stageEquity = stageData.reduce((s, r) => {
        if (r.total_investment && r.equity_percentage) {
          return s + r.total_investment * (r.equity_percentage / 100);
        }
        return s;
      }, 0);
      return {
        name: stage.label,
        color: stage.color,
        count: stageData.length,
        investment: stageInvestment,
        equity: stageEquity,
        financed: stageInvestment - stageEquity,
        trucks: stageData.reduce((s, r) => s + (r.total_trucks || 0), 0),
        power: stageData.reduce((s, r) => s + (r.contracted_power_kw || 0), 0),
        surface: stageData.reduce((s, r) => s + (r.surface_area_m2 || 0), 0),
        chargers: stageData.reduce((s, r) => s + (r.development_size_chargers || 0), 0),
        landCost: stageData.reduce((s, r) => s + (r.cost_land_purchase || 0), 0),
        devCost: stageData.reduce((s, r) => s + (r.cost_land_development || 0), 0),
        engCost: stageData.reduce((s, r) => s + ((r as any).cost_engineering || 0), 0),
        infraCost: stageData.reduce((s, r) => s + (r.cost_charging_infra || 0), 0),
      };
    });
  }, [sortedTableData]);
  
  // City breakdown data
  const cityChartData = useMemo(() => {
    const cityMap = new Map<string, any>();
    sortedTableData.forEach(row => {
      const city = row.city || 'Onbekend';
      const existing = cityMap.get(city) || { 
        name: city, count: 0, investment: 0, trucks: 0, equity: 0, power: 0, 
        surface: 0, chargers: 0, financed: 0 
      };
      existing.count++;
      existing.investment += row.total_investment || 0;
      existing.trucks += row.total_trucks || 0;
      existing.power += row.contracted_power_kw || 0;
      existing.surface += row.surface_area_m2 || 0;
      existing.chargers += row.development_size_chargers || 0;
      if (row.total_investment && row.equity_percentage) {
        const eq = row.total_investment * (row.equity_percentage / 100);
        existing.equity += eq;
        existing.financed += row.total_investment - eq;
      }
      cityMap.set(city, existing);
    });
    return Array.from(cityMap.values())
      .sort((a, b) => b[chartMetric] - a[chartMetric])
      .slice(0, 10);
  }, [sortedTableData, chartMetric]);
  
  // Ownership breakdown data
  const ownershipChartData = useMemo(() => {
    const types: Record<string, string> = { 
      koop: 'Koop', 
      huur: 'Huur', 
      huur_koopoptie: 'Huur+Koop', 
      in_eigendom: 'In eigendom' 
    };
    const typeMap = new Map<string, any>();
    
    sortedTableData.forEach(row => {
      const type = row.ownership_type || 'onbekend';
      const label = types[type] || 'Onbekend';
      const existing = typeMap.get(type) || { 
        name: label, count: 0, investment: 0, trucks: 0, equity: 0, power: 0,
        surface: 0, chargers: 0, financed: 0
      };
      existing.count++;
      existing.investment += row.total_investment || 0;
      existing.trucks += row.total_trucks || 0;
      existing.power += row.contracted_power_kw || 0;
      existing.surface += row.surface_area_m2 || 0;
      existing.chargers += row.development_size_chargers || 0;
      if (row.total_investment && row.equity_percentage) {
        const eq = row.total_investment * (row.equity_percentage / 100);
        existing.equity += eq;
        existing.financed += row.total_investment - eq;
      }
      typeMap.set(type, existing);
    });
    
    return Array.from(typeMap.values());
  }, [sortedTableData]);
  
  // Cost breakdown data
  const costBreakdownData = useMemo(() => {
    const totalLand = sortedTableData.reduce((s, r) => s + (r.cost_land_purchase || 0), 0);
    const totalDev = sortedTableData.reduce((s, r) => s + (r.cost_land_development || 0), 0);
    const totalEng = sortedTableData.reduce((s, r) => s + ((r as any).cost_engineering || 0), 0);
    const totalInfra = sortedTableData.reduce((s, r) => s + (r.cost_charging_infra || 0), 0);
    return [
      { name: 'Aankoop Grond', value: totalLand, color: '#0091ff' },
      { name: 'Aanleg Grond', value: totalDev, color: '#ff9100' },
      { name: 'Engineering', value: totalEng, color: '#9333ea' },
      { name: 'Laadinfra', value: totalInfra, color: '#28a745' },
    ].filter(d => d.value > 0);
  }, [sortedTableData]);

  const formatValue = (col: string, value: any, row: any) => {
    switch (col) {
      case 'ownership_type':
        if (!value) return '-';
        const types: Record<string, string> = { koop: 'Koop', huur: 'Huur', huur_koopoptie: 'Huur+Koop', in_eigendom: 'In eigendom' };
        return types[value] || value;
      case 'total_investment':
      case 'cost_land_purchase':
      case 'cost_land_development':
      case 'cost_engineering':
      case 'cost_charging_infra':
      case 'subsidy_amount':
        return value ? `€${value.toLocaleString('nl-NL', { maximumFractionDigits: 0 })}` : '-';
      case 'equity_percentage':
        return value ? `${value}%` : '-';
      case 'equity_amount':
        const eqAmount = row.total_investment && row.equity_percentage 
          ? row.total_investment * (row.equity_percentage / 100) 
          : null;
        return eqAmount ? `€${Math.round(eqAmount).toLocaleString('nl-NL')}` : '-';
      case 'financed_percentage':
        return row.equity_percentage != null ? `${100 - row.equity_percentage}%` : '-';
      case 'financed_amount':
        const finAmount = row.total_investment && row.equity_percentage != null
          ? row.total_investment * (1 - row.equity_percentage / 100) 
          : null;
        return finAmount ? `€${Math.round(finAmount).toLocaleString('nl-NL')}` : '-';
      case 'surface_area_m2':
      case 'contracted_power_kw':
      case 'development_size_chargers':
        return value ? value.toLocaleString('nl-NL') : '-';
      case 'stage':
        return faseLabel(getStageGroup(value));
      case 'interested_parties':
        return row.interested_parties || [];
      case 'total_trucks':
        return row.total_trucks || 0;
      case 'target_quarter':
        return value || '-';
      case 'notes':
        // Dynamic length based on slider
        return value ? (value.length > notesMaxLength ? value.substring(0, notesMaxLength) + '...' : value) : '-';
      default:
        return value || '-';
    }
  };
  
  const handleSort = (colId: string) => {
    if (sortColumn === colId) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(colId);
      setSortDirection('desc');
    }
  };

  const handleExportPNG = async () => {
    const targetRef = viewMode === 'charts' ? chartRef.current : tableRef.current;
    if (!targetRef) return;
    setExporting(true);
    
    const startTime = Date.now();
    console.log('[Export] Starting PNG export...');
    
    try {
      const clone = targetRef.cloneNode(true) as HTMLElement;
      
      clone.style.position = 'absolute';
      clone.style.left = '-9999px';
      clone.style.top = '0';
      clone.style.maxHeight = 'none';
      clone.style.overflow = 'visible';
      clone.style.width = targetRef.scrollWidth + 'px';
      
      document.body.appendChild(clone);
      
      // Wait for images with individual timeouts (max 1.5s per image)
      const images = clone.querySelectorAll('img');
      const imagePromises = Array.from(images).map((img, idx) => {
        if (img.complete && img.naturalHeight > 0) {
          return Promise.resolve();
        }
        
        return new Promise<void>((resolve) => {
          const timeout = setTimeout(() => {
            console.warn('[Export] Image timeout:', img.src?.substring(0, 50));
            // Hide broken image
            img.style.display = 'none';
            resolve();
          }, 1500);
          
          img.onload = () => {
            clearTimeout(timeout);
            resolve();
          };
          
          img.onerror = () => {
            clearTimeout(timeout);
            img.style.display = 'none';
            resolve();
          };
        });
      });
      
      // Wait for all images with global timeout (max 4s total)
      await Promise.race([
        Promise.all(imagePromises),
        new Promise(resolve => setTimeout(resolve, 4000))
      ]);
      
      console.log('[Export] Images ready in', Date.now() - startTime, 'ms');
      
      // Small delay for chart rendering
      if (viewMode === 'charts') {
        await new Promise(resolve => setTimeout(resolve, 300));
      }
      
      const canvas = await html2canvas(clone, {
        useCORS: true,
        allowTaint: false,
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
        windowWidth: clone.scrollWidth,
        windowHeight: clone.scrollHeight,
      });
      
      document.body.removeChild(clone);
      
      console.log('[Export] Canvas ready in', Date.now() - startTime, 'ms');
      
      canvas.toBlob((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `locaties-${viewMode}-${new Date().toISOString().split('T')[0]}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          console.log('[Export] Complete in', Date.now() - startTime, 'ms');
          toast({ title: 'Export succesvol' });
        }
      }, 'image/png');
      
    } catch (error) {
      console.error('[Export] Error:', error);
      toast({ title: 'Export mislukt', variant: 'destructive' });
    } finally {
      setExporting(false);
    }
  };

  const handleExportLegenda = async () => {
    // Collect unique organizations from all visible locations
    const uniqueOrgs = new Map<string, { name: string; logo: string | null }>();
    
    sortedTableData.forEach((row) => {
      row.interested_parties.forEach((party: any) => {
        if (!uniqueOrgs.has(party.orgName)) {
          uniqueOrgs.set(party.orgName, {
            name: party.orgName,
            logo: party.orgLogo,
          });
        }
      });
    });

    if (uniqueOrgs.size === 0) {
      toast({ title: 'Geen partijen gevonden', variant: 'destructive' });
      return;
    }

    // Build temporary legend table
    const legendContainer = document.createElement('div');
    legendContainer.style.cssText = 'background: white; padding: 24px; font-family: sans-serif; min-width: 300px;';
    legendContainer.innerHTML = `
      <h3 style="margin-bottom: 16px; color: #080c55; font-size: 16px; font-weight: 600;">Legenda - Geïnteresseerde Partijen</h3>
      <table style="border-collapse: collapse; width: 100%;">
        <tbody>
          ${Array.from(uniqueOrgs.values()).map(org => `
            <tr style="border-bottom: 1px solid #e5e7eb;">
              <td style="padding: 10px 12px; width: 48px; text-align: center;">
                ${org.logo 
                  ? `<img src="${org.logo}" style="height: 24px; width: 24px; object-fit: contain;" crossorigin="anonymous" />`
                  : `<div style="height: 24px; width: 24px; background: #f3f4f6; border-radius: 4px; display: flex; align-items: center; justify-content: center; font-size: 10px; font-weight: 600; color: #6b7280; margin: 0 auto;">${org.name.slice(0,2).toUpperCase()}</div>`
                }
              </td>
              <td style="padding: 10px 12px; font-size: 14px;">${org.name}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
      <div style="margin-top: 16px; padding-top: 12px; border-top: 1px solid #e5e7eb; text-align: right; font-size: 11px; color: #9ca3af;">
        GreenVision Charging © ${new Date().getFullYear()}
      </div>
    `;

    document.body.appendChild(legendContainer);
    
    try {
      // Wait for images to load
      const images = legendContainer.querySelectorAll('img');
      await Promise.all(Array.from(images).map(img => 
        new Promise<void>(resolve => {
          if (img.complete) resolve();
          else {
            img.onload = () => resolve();
            img.onerror = () => {
              img.style.display = 'none';
              resolve();
            };
            setTimeout(resolve, 1500);
          }
        })
      ));

      const canvas = await html2canvas(legendContainer, {
        useCORS: true,
        allowTaint: true,
        scale: 2,
        backgroundColor: '#ffffff',
      });
      
      canvas.toBlob((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `legenda-partijen-${new Date().toISOString().split('T')[0]}.png`;
          link.click();
          URL.revokeObjectURL(url);
          toast({ title: 'Legenda geëxporteerd' });
        }
      });
    } finally {
      document.body.removeChild(legendContainer);
    }
  };

  const handleExportXLSX = () => {
    // Use visibleColumns which respects the user's drag-and-drop order
    const cols = visibleColumns;
    const headers = cols.map(c => c.label);
    
    const rows = sortedTableData.map(row => 
      cols.map(col => {
        if (col.id === 'interested_parties') {
          return row.interested_parties.map((p: any) => `${p.orgName} (${p.trucks} trucks)`).join(', ');
        }
        // Keep numeric values as numbers for Excel
        if (['total_investment', 'equity_amount', 'financed_amount', 'cost_land_purchase', 
             'cost_land_development', 'cost_charging_infra', 'surface_area_m2', 
             'contracted_power_kw', 'total_trucks'].includes(col.id)) {
          const value = row[col.id];
          return typeof value === 'number' ? value : null;
        }
        if (['equity_percentage', 'financed_percentage'].includes(col.id)) {
          const value = row[col.id];
          return typeof value === 'number' ? value : null;
        }
        return formatValue(col.id, row[col.id], row);
      })
    );
    
    // Add totals row
    const totalRow = cols.map(col => {
      if (col.id === 'name') return 'TOTAAL';
      if (col.id === 'total_trucks') return totals.trucks;
      if (col.id === 'total_investment') return totals.investment;
      return '';
    });
    rows.push(totalRow);
    
    // Create worksheet
    const wsData = [headers, ...rows];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    
    // Set column widths based on content type
    ws['!cols'] = cols.map(col => ({
      wch: col.id === 'name' ? 35 : 
           col.id === 'interested_parties' ? 45 : 
           col.id === 'locode' ? 12 :
           col.id === 'city' ? 18 :
           col.id === 'address' ? 35 :
           col.id === 'stage' ? 15 : 16
    }));
    
    // Create workbook and download
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Laadlocaties');
    XLSX.writeFile(wb, `laadlocaties-${new Date().toISOString().split('T')[0]}.xlsx`);
    
    toast({ title: 'Excel geëxporteerd', description: 'Het bestand is gedownload.' });
  };

  // Bulk slide export for all visible locations using LocationSlideTemplate
  const handleBulkSlideExport = async (skipDrawingCheck = false) => {
    if (sortedTableData.length === 0) {
      toast({ title: 'Geen locaties', description: 'Selecteer eerst locaties om te exporteren.', variant: 'destructive' });
      return;
    }

    // Check for locations without drawings (unless skip is requested)
    if (!skipDrawingCheck) {
      // First, check which locations have drawings
      const { data: allDrawings } = await supabase
        .from('location_documents')
        .select('location_id')
        .eq('document_type', 'drawing')
        .in('location_id', sortedTableData.map(l => l.id));
      
      const locationsWithDrawings = new Set((allDrawings || []).map(d => d.location_id));
      const locationsWithoutDrawing = sortedTableData.filter(l => !locationsWithDrawings.has(l.id));
      
      if (locationsWithoutDrawing.length > 0) {
        setMissingDrawingLocations(locationsWithoutDrawing);
        setShowBulkMissingDrawingAlert(true);
        return;
      }
    }

    setShowBulkMissingDrawingAlert(false);
    setExportingSlides(true);
    
    try {
      const zip = new JSZip();
      
      // Fetch mapbox token once
      let mapboxToken: string | null = null;
      try {
        const { data: tokenData } = await supabase.functions.invoke('get-mapbox-token');
        mapboxToken = tokenData?.token || null;
      } catch (e) {
        console.warn('Could not fetch mapbox token:', e);
      }
      
      // For each location, fetch data and generate slide
      for (const location of sortedTableData) {
        // Fetch interested parties for this location
        const { data: oppLinks } = await supabase
          .from('opportunity_locations')
          .select(`
            opportunity:charging_opportunities(
              organization:organizations(name, logo_url, favicon_url, cached_favicon_url)
            )
          `)
          .eq('location_id', location.id);
        
        const interestedParties = (oppLinks || [])
          .map((link: any) => ({ 
            orgName: link.opportunity?.organization?.name || 'Onbekend',
            orgLogo: link.opportunity?.organization?.cached_favicon_url || 
                     link.opportunity?.organization?.logo_url || 
                     link.opportunity?.organization?.favicon_url || null,
          }))
          .filter((p: any) => p.orgName !== 'Onbekend');

        // Use site_sketch_url directly from location data (like individual slide generator)
        const siteDrawingUrl = location.site_sketch_url || null;

        // Create a hidden container for rendering the slide at full size
        const container = document.createElement('div');
        container.style.cssText = 'position: absolute; left: -9999px; top: 0;';
        document.body.appendChild(container);

        // Render LocationSlideTemplate into container using React 18's createRoot
        const root = createRoot(container);
        
        await new Promise<void>((resolve) => {
          root.render(
            <LocationSlideTemplate
              location={location}
              interestedParties={interestedParties}
              siteDrawingUrl={siteDrawingUrl}
              mapboxToken={mapboxToken}
            />
          );
          // Wait for render and images
          setTimeout(resolve, 300);
        });

        // Wait for images to load (with timeout, don't hide essential images)
        const images = container.querySelectorAll('img');
        await Promise.all(
          Array.from(images).map((img) =>
            new Promise<void>((resolve) => {
              if (img.complete && img.naturalHeight > 0) {
                resolve();
                return;
              }
              
              // Essential images (site drawing, map) should never be hidden
              const isEssentialImage = img.alt === 'Site Layout' || img.alt === 'Location Map';
              const timeoutMs = isEssentialImage ? 5000 : 2000;
              
              const timeout = setTimeout(() => {
                // Only hide favicons on timeout, not essential images
                if (!isEssentialImage) {
                  img.style.display = 'none';
                }
                resolve();
              }, timeoutMs);
              
              img.onload = () => { clearTimeout(timeout); resolve(); };
              img.onerror = () => { 
                clearTimeout(timeout); 
                // Only hide favicons on error, not essential images
                if (!isEssentialImage) {
                  img.style.display = 'none';
                }
                resolve(); 
              };
            })
          )
        );

        // Extra wait for fonts/rendering
        await new Promise(r => setTimeout(r, 100));

        // Capture as canvas
        const slideEl = container.querySelector('div');
        if (slideEl) {
          const canvas = await html2canvas(slideEl as HTMLElement, {
            useCORS: true,
            allowTaint: false,
            scale: 1,
            width: 1920,
            height: 1080,
            backgroundColor: '#080c55',
          });

          // Convert to blob and add to ZIP
          const blob = await new Promise<Blob>((resolve) => {
            canvas.toBlob((b) => resolve(b!), 'image/png', 0.95);
          });

          const fileName = `${location.locode || 'LOC'}-${location.name.replace(/[^a-zA-Z0-9]/g, '_')}.png`;
          zip.file(fileName, blob);
        }

        // Cleanup
        root.unmount();
        document.body.removeChild(container);
      }

      // Generate and download ZIP
      const content = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(content);
      const link = document.createElement('a');
      link.href = url;
      link.download = `locatie-slides-${new Date().toISOString().split('T')[0]}.zip`;
      link.click();
      URL.revokeObjectURL(url);

      toast({ 
        title: 'Slides geëxporteerd', 
        description: `${sortedTableData.length} slides in ZIP bestand.` 
      });
    } catch (error) {
      console.error('Bulk slide export failed:', error);
      toast({ 
        title: 'Export mislukt', 
        description: 'Er ging iets mis bij het genereren van de slides.',
        variant: 'destructive'
      });
    } finally {
      setExportingSlides(false);
    }
  };

  // Toggle all locations in a stage
  const toggleStageLocations = (stageId: string, checked: boolean) => {
    const stageLocations = locationsByStage[stageId] || [];
    const newSet = new Set(visibleLocationIds);
    stageLocations.forEach((loc: any) => {
      if (checked) {
        newSet.add(loc.id);
      } else {
        newSet.delete(loc.id);
      }
    });
    setVisibleLocationIds(newSet);
  };

  // Check if all/some locations in a stage are visible
  const getStageCheckState = (stageId: string) => {
    const stageLocations = locationsByStage[stageId] || [];
    if (stageLocations.length === 0) return { all: false, some: false };
    const visibleCount = stageLocations.filter((loc: any) => visibleLocationIds.has(loc.id)).length;
    return {
      all: visibleCount === stageLocations.length,
      some: visibleCount > 0 && visibleCount < stageLocations.length,
    };
  };

  // Visible columns - derived from columnOrder (respects drag order)
  const visibleColumns = useMemo(() => 
    columnOrder
      .filter(id => selectedColumns.has(id))
      .map(id => COLUMNS.find(c => c.id === id)!)
      .filter(Boolean),
    [columnOrder, selectedColumns]
  );
  
  // Handle column toggle - adds/removes from both selectedColumns and columnOrder
  const handleColumnToggle = (columnId: string, checked: boolean) => {
    const newSelected = new Set(selectedColumns);
    if (checked) {
      newSelected.add(columnId);
      // Add to end of column order if not already present
      if (!columnOrder.includes(columnId)) {
        setColumnOrder(prev => [...prev, columnId]);
      }
    } else {
      newSelected.delete(columnId);
      // Remove from column order
      setColumnOrder(prev => prev.filter(id => id !== columnId));
    }
    setSelectedColumns(newSelected);
  };
  
  // Handle drag end for column reordering
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setColumnOrder(prev => {
        const oldIndex = prev.indexOf(active.id as string);
        const newIndex = prev.indexOf(over.id as string);
        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  };
  
  const formatChartValue = (value: number) => {
    if (['investment', 'equity', 'financed'].includes(chartMetric)) {
      if (value >= 1000000) return `€${(value / 1000000).toFixed(1)}M`;
      if (value >= 1000) return `€${(value / 1000).toFixed(0)}K`;
      return `€${value}`;
    }
    return value.toLocaleString('nl-NL');
  };
  
  const getActiveChartData = () => {
    switch (chartDataView) {
      case 'city': return cityChartData;
      case 'ownership': return ownershipChartData;
      default: return chartData;
    }
  };

  const renderChart = (data: any[], height = 280) => {
    if (!data.length) return <div className="flex items-center justify-center h-full text-muted-foreground">Geen data</div>;

    switch (chartType) {
      case 'pie':
        return (
          <ResponsiveContainer width="100%" height={height}>
            <PieChart margin={{ top: 20, right: 20, left: 20, bottom: 20 }}>
              <Pie
                data={data}
                dataKey={chartMetric}
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={Math.min(height / 3.5, 75)}
                label={({ value }) => formatChartValue(value)}
                labelLine={{ stroke: '#666', strokeWidth: 1 }}
              >
                {data.map((entry, i) => (
                  <Cell key={i} fill={entry.color || CHART_COLORS[i % CHART_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(v: number) => formatChartValue(v)} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        );

      case 'donut':
        return (
          <ResponsiveContainer width="100%" height={height}>
            <PieChart margin={{ top: 20, right: 20, left: 20, bottom: 20 }}>
              <Pie
                data={data}
                dataKey={chartMetric}
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={Math.min(height / 6, 45)}
                outerRadius={Math.min(height / 3.5, 80)}
                paddingAngle={2}
                label={({ value }) => formatChartValue(value)}
                labelLine={{ stroke: '#666', strokeWidth: 1 }}
              >
                {data.map((entry, i) => (
                  <Cell key={i} fill={entry.color || CHART_COLORS[i % CHART_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(v: number) => formatChartValue(v)} />
              <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
            </PieChart>
          </ResponsiveContainer>
        );

      case 'radial':
        const maxValue = Math.max(...data.map(d => d[chartMetric]));
        const radialData = data.map((d, i) => ({
          ...d,
          fill: d.color || CHART_COLORS[i % CHART_COLORS.length],
          percentage: (d[chartMetric] / maxValue) * 100,
        }));
        return (
          <ResponsiveContainer width="100%" height={height}>
            <RadialBarChart 
              innerRadius="20%" 
              outerRadius="90%" 
              data={radialData} 
              startAngle={180} 
              endAngle={0}
            >
              <RadialBar
                background
                dataKey={chartMetric}
                label={{ position: 'insideStart', fill: '#fff', fontSize: 12 }}
              />
              <Tooltip formatter={(v: number) => formatChartValue(v)} />
              <Legend iconSize={10} layout="horizontal" verticalAlign="bottom" />
            </RadialBarChart>
          </ResponsiveContainer>
        );

      case 'stacked':
        return (
          <ResponsiveContainer width="100%" height={height}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis tickFormatter={(v) => formatChartValue(v)} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v: number) => formatChartValue(v)} />
              <Legend />
              <Bar dataKey="investment" name="Investering" stackId="a" fill="#080c55" radius={[0, 0, 0, 0]} />
              <Bar dataKey="equity" name="Equity" stackId="a" fill="#00a4d4" radius={[0, 0, 0, 0]} />
              <Bar dataKey="financed" name="Gefinancierd" stackId="a" fill="#28a745" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        );

      case 'area':
        return (
          <ResponsiveContainer width="100%" height={height}>
            <AreaChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis tickFormatter={(v) => formatChartValue(v)} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v: number) => formatChartValue(v)} />
              <Legend />
              <Area 
                type="monotone" 
                dataKey={chartMetric} 
                stroke="#080c55" 
                fill="#00a4d4" 
                fillOpacity={0.3} 
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        );

      case 'line':
        return (
          <ResponsiveContainer width="100%" height={height}>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis tickFormatter={(v) => formatChartValue(v)} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v: number) => formatChartValue(v)} />
              <Legend />
              <Line 
                type="monotone" 
                dataKey={chartMetric}
                name={METRICS.find(m => m.id === chartMetric)?.label}
                stroke="#080c55" 
                strokeWidth={3}
                dot={{ fill: '#080c55', r: 5 }}
                activeDot={{ r: 8, fill: '#00a4d4' }}
              />
            </LineChart>
          </ResponsiveContainer>
        );

      default: // bar
        return (
          <ResponsiveContainer width="100%" height={height}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis tickFormatter={(v) => formatChartValue(v)} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v: number) => formatChartValue(v)} />
              <Bar dataKey={chartMetric} radius={[4, 4, 0, 0]}>
                {data.map((entry, i) => (
                  <Cell key={i} fill={entry.color || CHART_COLORS[i % CHART_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        );
    }
  };

  return (
    <div className="h-screen flex flex-col bg-background">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b bg-background shrink-0">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <TableIcon className="h-5 w-5" />
              Tabel
            </h1>
            <p className="text-sm text-muted-foreground">Exporteer laadlocaties in aangepaste tabelvorm</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => handleBulkSlideExport(false)} 
            disabled={exportingSlides || isLoading || sortedTableData.length === 0}
          >
            <Presentation className="h-4 w-4 mr-2" />
            {exportingSlides ? 'Exporteren...' : `Slides (${sortedTableData.length})`}
          </Button>
          <Button variant="outline" onClick={handleExportLegenda} disabled={isLoading}>
            <Users className="h-4 w-4 mr-2" />
            Legenda
          </Button>
          <Button variant="outline" onClick={handleExportXLSX} disabled={isLoading}>
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Excel
          </Button>
          <Button onClick={handleExportPNG} disabled={exporting || isLoading}>
            <Download className="h-4 w-4 mr-2" />
            {exporting ? 'Exporteren...' : 'PNG'}
          </Button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 p-3 border-b bg-muted/30 shrink-0">
        {/* View toggle */}
        <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as any)} className="h-9">
          <TabsList className="h-9">
            <TabsTrigger value="table" className="gap-1 h-8">
              <TableIcon className="h-4 w-4" />
              Tabel
            </TabsTrigger>
            <TabsTrigger value="charts" className="gap-1 h-8">
              <BarChart3 className="h-4 w-4" />
              Grafieken
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="h-6 w-px bg-border" />

        {/* Chart controls (only in charts mode) */}
        {viewMode === 'charts' && (
          <>
            {/* Chart Type Buttons */}
            <div className="flex gap-1 p-1 bg-background rounded-lg border">
              {CHART_TYPES.map(type => (
                <Button
                  key={type.id}
                  variant={chartType === type.id ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setChartType(type.id as ChartType)}
                  className="h-7 w-7 p-0"
                  title={type.label}
                >
                  <type.icon className="h-4 w-4" />
                </Button>
              ))}
            </div>
            
            {/* Metric Selector */}
            <Select value={chartMetric} onValueChange={(v) => setChartMetric(v as ChartMetric)}>
              <SelectTrigger className="w-40 h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {METRICS.map(metric => (
                  <SelectItem key={metric.id} value={metric.id}>
                    <div className="flex items-center gap-2">
                      <metric.icon className="h-4 w-4" />
                      {metric.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="h-6 w-px bg-border" />
          </>
        )}

        {/* Summary badges */}
        <div className="flex flex-wrap gap-2">
          <Badge variant="secondary" className="gap-1 text-xs px-2 py-1">
            <MapPin className="h-3 w-3" />
            {sortedTableData.length} locaties
          </Badge>
          <Badge variant="secondary" className="gap-1 text-xs px-2 py-1">
            <Truck className="h-3 w-3" />
            {totals.trucks} trucks
          </Badge>
          <Badge variant="secondary" className="gap-1 text-xs px-2 py-1">
            <Euro className="h-3 w-3" />
            €{(totals.investment / 1000000).toFixed(1)}M
          </Badge>
        </div>

        <div className="flex-1" />

        {/* Theme selector */}
        <Select value={theme} onValueChange={setTheme}>
          <SelectTrigger className="w-44 h-9">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {THEMES.map(t => (
              <SelectItem key={t.id} value={t.id}>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded" style={{ backgroundColor: t.primary }} />
                  {t.label}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Sort reset */}
        {sortColumn && (
          <Button 
            variant="outline" 
            size="sm"
            className="h-9"
            onClick={() => setSortColumn(null)}
          >
            <RotateCcw className="h-4 w-4 mr-1" />
            Reset
          </Button>
        )}
      </div>

      {/* Main content with filter panel */}
      <div className="flex-1 flex relative overflow-hidden">
        {/* Filter panel toggle button (when closed) */}
        <Button
          variant="outline"
          size="sm"
          className={cn(
            "absolute left-0 top-4 z-50 rounded-l-none shadow-lg transition-opacity",
            filtersPanelOpen ? "opacity-0 pointer-events-none" : "opacity-100"
          )}
          onClick={() => setFiltersPanelOpen(true)}
        >
          <Filter className="h-4 w-4 mr-1" />
          <ChevronRight className="h-4 w-4" />
        </Button>

        {/* Collapsible filter panel */}
        <div className={cn(
          "w-72 border-r bg-background shrink-0 flex flex-col transition-all duration-300",
          filtersPanelOpen ? "translate-x-0" : "-translate-x-full absolute left-0 top-0 bottom-0 z-40"
        )}>
          <div className="p-3 border-b flex items-center justify-between shrink-0">
            <h3 className="font-semibold text-sm flex items-center gap-2">
              <Filter className="h-4 w-4" />
              Filters
            </h3>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setFiltersPanelOpen(false)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
          </div>
          
          <ScrollArea className="flex-1">
            <div className="p-3 space-y-4">
              {/* T2.5 / scepticus-ronde — wat er van de tabel af is, staat er
                  nu bij, met het getal erbij. Dit is het overzicht dat naar
                  buiten gaat; een stille aftrekking hoort hier het minst
                  thuis. */}
              <div className="rounded-md border bg-muted/30 p-2 space-y-2">
                <p className="text-xs">
                  <span className="font-medium">
                    {slapendTelling.zichtbaar} van {slapendTelling.totaal} Kavels in beeld
                  </span>
                </p>
                {slapendTelling.slapend > 0 && (
                  <div className="flex items-center justify-between gap-2">
                    <Label htmlFor="gauw-toon-slapend" className="cursor-pointer text-xs">
                      Slapend tonen ({slapendTelling.zichtbaarSlapend}/{slapendTelling.slapend})
                    </Label>
                    <Switch
                      id="gauw-toon-slapend"
                      checked={toonSlapend}
                      onCheckedChange={zetSlapendZichtbaar}
                    />
                  </div>
                )}
              </div>

              {/* Location filters by stage */}
              <div>
                <Label className="font-medium text-xs text-muted-foreground uppercase mb-2 block">
                  Locaties per fase
                </Label>
                <div className="space-y-1">
                  {/* Alle fasen, ook Verloren en de restgroep — anders is een
                      Kavel daarin onbereikbaar (T2.5). Een lege restgroep
                      slaan we over, die hoeft niet in beeld. */}
                  {STAGES.filter(s => s.id !== 'onbekend' || (locationsByStage.onbekend?.length ?? 0) > 0).map(stage => {
                    const stageLocations = locationsByStage[stage.id] || [];
                    const { all, some } = getStageCheckState(stage.id);
                    const isExpanded = expandedStages.has(stage.id);
                    
                    return (
                      <Collapsible 
                        key={stage.id}
                        open={isExpanded}
                        onOpenChange={(open) => {
                          const newSet = new Set(expandedStages);
                          if (open) newSet.add(stage.id);
                          else newSet.delete(stage.id);
                          setExpandedStages(newSet);
                        }}
                      >
                        <div className="flex items-center gap-2 py-1">
                          <Checkbox
                            checked={all}
                            // @ts-ignore - data-state works for indeterminate
                            data-state={some ? 'indeterminate' : all ? 'checked' : 'unchecked'}
                            onCheckedChange={(checked) => toggleStageLocations(stage.id, !!checked)}
                          />
                          <CollapsibleTrigger asChild>
                            <button className="flex items-center gap-2 flex-1 text-left hover:bg-muted/50 rounded px-1 py-0.5">
                              <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: stage.color }} />
                              <span className="text-sm font-medium">{stage.label}</span>
                              <Badge variant="secondary" className="ml-auto text-xs h-5">
                                {stageLocations.filter((l: any) => visibleLocationIds.has(l.id)).length}/{stageLocations.length}
                              </Badge>
                              <ChevronDown className={cn("h-4 w-4 transition-transform", isExpanded && "rotate-180")} />
                            </button>
                          </CollapsibleTrigger>
                        </div>
                        
                        <CollapsibleContent className="pl-6 space-y-0.5 pb-2">
                          {stageLocations.map((loc: any) => (
                            <Label key={loc.id} className="flex items-center gap-2 cursor-pointer text-xs py-0.5 hover:bg-muted/30 rounded px-1">
                              <Checkbox
                                checked={visibleLocationIds.has(loc.id)}
                                onCheckedChange={(checked) => {
                                  const newSet = new Set(visibleLocationIds);
                                  if (checked) newSet.add(loc.id);
                                  else newSet.delete(loc.id);
                                  setVisibleLocationIds(newSet);
                                }}
                              />
                              <span className="text-muted-foreground font-mono w-12 shrink-0">{loc.locode || '-'}</span>
                              <span className="truncate">{loc.name}</span>
                            </Label>
                          ))}
                        </CollapsibleContent>
                      </Collapsible>
                    );
                  })}
                </div>
              </div>

              <div className="h-px bg-border" />

              {/* Column Selection */}
              <div>
                <Label className="font-medium text-xs text-muted-foreground uppercase mb-2 block">
                  Kolommen
                </Label>
                <div className="space-y-0.5 max-h-48 overflow-y-auto">
                  {COLUMNS.map(col => (
                    <Label key={col.id} className="flex items-center gap-2 cursor-pointer text-xs py-0.5">
                      <Checkbox
                        checked={selectedColumns.has(col.id)}
                        onCheckedChange={(checked) => handleColumnToggle(col.id, checked as boolean)}
                      />
                      {col.label}
                    </Label>
                  ))}
                </div>
              </div>

              <div className="h-px bg-border" />

              {/* Table styling options */}
              <div className="space-y-3">
                <Label className="font-medium text-xs text-muted-foreground uppercase block">
                  Tabel Opmaak
                </Label>
                
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs">Header stijl</span>
                    <Select value={headerStyle} onValueChange={(v: any) => setHeaderStyle(v)}>
                      <SelectTrigger className="w-24 h-7 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bold">Bold</SelectItem>
                        <SelectItem value="colored">Gekleurd</SelectItem>
                        <SelectItem value="underlined">Onderstreept</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-xs">Rand stijl</span>
                    <Select value={borderStyle} onValueChange={(v: any) => setBorderStyle(v)}>
                      <SelectTrigger className="w-24 h-7 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Geen</SelectItem>
                        <SelectItem value="light">Licht</SelectItem>
                        <SelectItem value="full">Vol</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-xs">Tekstgrootte</span>
                    <Select value={fontSize} onValueChange={(v: any) => setFontSize(v)}>
                      <SelectTrigger className="w-24 h-7 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="small">Klein</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="large">Groot</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <Label className="flex items-center gap-2 cursor-pointer text-xs pt-1">
                    <Checkbox
                      checked={alternatingRows}
                      onCheckedChange={(checked) => setAlternatingRows(checked as boolean)}
                    />
                    Alternerende rij kleuren
                  </Label>
                  
                  <Label className="flex items-center gap-2 cursor-pointer text-xs pt-1">
                    <Checkbox
                      checked={showTrucksPerParty}
                      onCheckedChange={(checked) => setShowTrucksPerParty(checked as boolean)}
                    />
                    Toon trucks per partij
                  </Label>
                  
                  {/* Notes length slider - only shown when notes column is selected */}
                  {selectedColumns.has('notes') && (
                    <div className="space-y-2 pt-2 border-t">
                      <div className="flex items-center justify-between">
                        <span className="text-xs">Notities lengte</span>
                        <span className="text-xs text-muted-foreground font-mono">
                          {notesMaxLength} tekens
                        </span>
                      </div>
                      <Slider
                        value={[notesMaxLength]}
                        onValueChange={(v) => setNotesMaxLength(v[0])}
                        min={20}
                        max={200}
                        step={10}
                        className="w-full"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </ScrollArea>
        </div>

        {/* Main content area */}
        <div className="flex-1 overflow-auto p-4">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : viewMode === 'table' ? (
            <div ref={tableRef} className="p-4 bg-white rounded-lg shadow-sm">
              {/* Header with logo */}
              <div className="flex items-center gap-3 mb-4 pb-3 border-b" style={{ borderColor: currentTheme.primary }}>
                <img src={gvccLogo} alt="GreenVision Charging" className="h-8" />
                <div>
                  <h2 className="font-semibold" style={{ color: currentTheme.primary }}>
                    Laadlocaties Overzicht
                  </h2>
                  <p className="text-xs text-muted-foreground">
                    {new Date().toLocaleDateString('nl-NL', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </p>
                </div>
              </div>

              {/* Table */}
              <table 
                className={`w-full ${fontSizeClass}`}
                style={{ 
                  borderCollapse: 'collapse',
                  border: borderStyle === 'full' ? `2px solid ${currentTheme.primary}` : 'none',
                }}
              >
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={visibleColumns.map(c => c.id)} strategy={horizontalListSortingStrategy}>
                    <thead>
                      <tr
                        style={{
                          backgroundColor: headerStyle === 'colored' ? currentTheme.primary : 'transparent',
                          color: headerStyle === 'colored' ? 'white' : currentTheme.primary,
                          borderBottom: headerStyle === 'underlined' ? `2px solid ${currentTheme.primary}` : 'none',
                        }}
                      >
                        {visibleColumns.map(col => (
                          <SortableHeader
                            key={col.id}
                            column={col}
                            headerStyle={headerStyle}
                            borderStyle={borderStyle}
                            currentTheme={currentTheme}
                            sortColumn={sortColumn}
                            sortDirection={sortDirection}
                            onSort={handleSort}
                          />
                        ))}
                      </tr>
                    </thead>
                  </SortableContext>
                </DndContext>
                <tbody>
                  {sortedTableData.map((row, idx) => (
                    <tr
                      key={row.id}
                      style={{
                        backgroundColor: alternatingRows && idx % 2 === 1 ? currentTheme.secondary : 'white',
                        borderBottom: borderStyle !== 'none' ? `1px solid ${currentTheme.primary}20` : 'none',
                      }}
                    >
                      {visibleColumns.map(col => (
                        <td
                          key={col.id}
                          className="p-2"
                          style={{
                            borderRight: borderStyle === 'full' ? `1px solid ${currentTheme.primary}20` : 'none',
                          }}
                        >
                          {col.id === 'interested_parties' ? (
                            <div className="flex flex-wrap gap-1">
                              {row.interested_parties.map((party: any, i: number) => (
                                <div key={i} className="flex flex-col items-center">
                                  {party.orgLogo ? (
                                    <img 
                                      src={party.orgLogo} 
                                      alt={party.orgName}
                                      className="h-6 w-6 object-contain" 
                                      crossOrigin="anonymous"
                                      loading="lazy"
                                      referrerPolicy="no-referrer"
                                      onError={async (e) => {
                                        const img = e.currentTarget;
                                        const domain = party.domain;
                                        
                                        // If we have a domain and haven't tried lazy caching yet
                                        if (domain && !failedDomains.has(domain) && !img.dataset.retried) {
                                          img.dataset.retried = 'true';
                                          console.log('[Favicon] Image failed, trying lazy cache:', domain);
                                          
                                          const cachedUrl = await cacheFaviconLazy(domain);
                                          if (cachedUrl) {
                                            // Retry with fresh URL (add cache-bust)
                                            img.src = cachedUrl + '?t=' + Date.now();
                                            return;
                                          }
                                        }
                                        
                                        // Show fallback
                                        img.style.display = 'none';
                                        const fallback = img.nextElementSibling as HTMLElement;
                                        if (fallback) fallback.style.display = 'flex';
                                      }}
                                    />
                                  ) : null}
                                  {/* Fallback badge - shown if no logo or on error */}
                                  <Badge 
                                    variant="outline" 
                                    className="text-xs justify-center items-center"
                                    style={{ display: party.orgLogo ? 'none' : 'flex' }}
                                  >
                                    {party.orgName.slice(0, 3)}
                                  </Badge>
                                  {showTrucksPerParty && party.trucks > 0 && (
                                    <span className="text-[10px] text-muted-foreground">{party.trucks}</span>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : col.id === 'total_trucks' ? (
                            <div className="flex items-center gap-1 font-medium">
                              <Truck className="h-3 w-3" />
                              {row.total_trucks || 0}
                            </div>
                          ) : (
                            formatValue(col.id, row[col.id], row)
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                  {/* Total row */}
                  <tr
                    style={{
                      backgroundColor: currentTheme.primary,
                      color: 'white',
                      fontWeight: 'bold',
                    }}
                  >
                    {visibleColumns.map((col, i) => (
                      <td key={col.id} className="p-2">
                        {i === 0 ? 'TOTAAL' : 
                         col.id === 'total_trucks' ? (
                          <div className="flex items-center gap-1">
                            <Truck className="h-3 w-3" />
                            {totals.trucks}
                          </div>
                         ) : 
                         col.id === 'surface_area_m2' ? `${totals.surface.toLocaleString('nl-NL')} m²` :
                         col.id === 'total_investment' ? `€${totals.investment.toLocaleString('nl-NL')}` :
                         col.id === 'equity_amount' ? `€${Math.round(totals.equity).toLocaleString('nl-NL')}` :
                         col.id === 'financed_amount' ? `€${Math.round(totals.financed).toLocaleString('nl-NL')}` :
                         col.id === 'equity_percentage' ? `${totals.equityPercentage.toFixed(1)}%` :
                         col.id === 'financed_percentage' ? `${totals.financedPercentage.toFixed(1)}%` :
                         col.id === 'contracted_power_kw' ? `${totals.power.toLocaleString('nl-NL')} kW` :
                         col.id === 'development_size_chargers' ? totals.chargers :
                         col.id === 'cost_land_purchase' ? `€${totals.landCost.toLocaleString('nl-NL')}` :
                         col.id === 'cost_land_development' ? `€${totals.devCost.toLocaleString('nl-NL')}` :
                         col.id === 'cost_engineering' ? `€${totals.engCost.toLocaleString('nl-NL')}` :
                         col.id === 'cost_charging_infra' ? `€${totals.infraCost.toLocaleString('nl-NL')}` :
                         col.id === 'subsidy_amount' ? `€${totals.subsidy.toLocaleString('nl-NL')}` : ''}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>

              {/* Footer */}
              <div className="flex items-center justify-between mt-4 pt-3 border-t text-xs text-muted-foreground">
                <span>{sortedTableData.length} locaties</span>
                <span>GreenVision Charging © {new Date().getFullYear()}</span>
              </div>
            </div>
          ) : (
            /* Charts View */
            <div ref={chartRef} className="p-4 bg-white rounded-lg shadow-sm space-y-6">
              {/* Data View Tabs */}
              <Tabs value={chartDataView} onValueChange={(v) => setChartDataView(v as ChartDataView)}>
                <TabsList className="mb-4">
                  <TabsTrigger value="stage">Per Stage</TabsTrigger>
                  <TabsTrigger value="city">Per Stad</TabsTrigger>
                  <TabsTrigger value="ownership">Per Eigendom</TabsTrigger>
                  <TabsTrigger value="costs">Kosten</TabsTrigger>
                  <TabsTrigger value="comparison">Vergelijking</TabsTrigger>
                </TabsList>
                
                <TabsContent value="stage" className="space-y-6">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <Card>
                      <CardHeader className="py-3">
                        <CardTitle className="text-sm font-medium">
                          {METRICS.find(m => m.id === chartMetric)?.label} per Stage
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="h-72">
                        {renderChart(chartData)}
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardHeader className="py-3">
                        <CardTitle className="text-sm font-medium">Investering vs Equity per Stage</CardTitle>
                      </CardHeader>
                      <CardContent className="h-72">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                            <YAxis tickFormatter={(v) => `€${(v/1000000).toFixed(0)}M`} tick={{ fontSize: 11 }} />
                            <Tooltip formatter={(v: number) => `€${(v/1000000).toFixed(2)}M`} />
                            <Legend />
                            <Bar dataKey="investment" name="Investering" fill="#080c55" radius={[4, 4, 0, 0]} />
                            <Bar dataKey="equity" name="Equity" fill="#00a4d4" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>
                
                <TabsContent value="city" className="space-y-6">
                  <Card>
                    <CardHeader className="py-3">
                      <CardTitle className="text-sm font-medium">Top 10 Steden - {METRICS.find(m => m.id === chartMetric)?.label}</CardTitle>
                    </CardHeader>
                    <CardContent className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={cityChartData} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" tickFormatter={(v) => formatChartValue(v)} tick={{ fontSize: 11 }} />
                          <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 12 }} />
                          <Tooltip formatter={(v: number) => formatChartValue(v)} />
                          <Bar dataKey={chartMetric} fill="#080c55" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </TabsContent>
                
                <TabsContent value="ownership" className="space-y-6">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <Card>
                      <CardHeader className="py-3">
                        <CardTitle className="text-sm font-medium">
                          {METRICS.find(m => m.id === chartMetric)?.label} per Eigendomstype
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="h-72">
                        {renderChart(ownershipChartData)}
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardHeader className="py-3">
                        <CardTitle className="text-sm font-medium">Eigendomsverdeling (Aantal)</CardTitle>
                      </CardHeader>
                      <CardContent className="h-72">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={ownershipChartData}
                              dataKey="count"
                              nameKey="name"
                              cx="50%"
                              cy="50%"
                              innerRadius={50}
                              outerRadius={90}
                              paddingAngle={2}
                              label={({ value }) => value}
                            >
                              {ownershipChartData.map((_, i) => (
                                <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>
                
                <TabsContent value="costs" className="space-y-6">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <Card>
                      <CardHeader className="py-3">
                        <CardTitle className="text-sm font-medium">Kostenverdeling Totaal</CardTitle>
                      </CardHeader>
                      <CardContent className="h-72">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart margin={{ top: 20, right: 20, left: 20, bottom: 20 }}>
                            <Pie
                              data={costBreakdownData}
                              dataKey="value"
                              nameKey="name"
                              cx="50%"
                              cy="50%"
                              innerRadius={45}
                              outerRadius={80}
                              paddingAngle={2}
                              label={({ value }) => `€${(value/1000000).toFixed(1)}M`}
                              labelLine={{ stroke: '#666', strokeWidth: 1 }}
                            >
                              {costBreakdownData.map((entry, i) => (
                                <Cell key={i} fill={entry.color} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(v: number) => `€${(v/1000000).toFixed(2)}M`} />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardHeader className="py-3">
                        <CardTitle className="text-sm font-medium">Kosten per Stage</CardTitle>
                      </CardHeader>
                      <CardContent className="h-72">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                            <YAxis tickFormatter={(v) => `€${(v/1000000).toFixed(0)}M`} tick={{ fontSize: 11 }} />
                            <Tooltip formatter={(v: number) => `€${(v/1000000).toFixed(2)}M`} />
                            <Legend />
                            <Bar dataKey="landCost" name="Grond Aankoop" stackId="a" fill="#0091ff" />
                            <Bar dataKey="devCost" name="Grond Aanleg" stackId="a" fill="#ff9100" />
                            <Bar dataKey="engCost" name="Engineering" stackId="a" fill="#9333ea" />
                            <Bar dataKey="infraCost" name="Laadinfra" stackId="a" fill="#28a745" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>
                
                <TabsContent value="comparison" className="space-y-6">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <Card>
                      <CardHeader className="py-3">
                        <CardTitle className="text-sm font-medium">Investering, Equity & Gefinancierd</CardTitle>
                      </CardHeader>
                      <CardContent className="h-72">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                            <YAxis tickFormatter={(v) => `€${(v/1000000).toFixed(0)}M`} tick={{ fontSize: 11 }} />
                            <Tooltip formatter={(v: number) => `€${(v/1000000).toFixed(2)}M`} />
                            <Legend />
                            <Bar dataKey="investment" name="Totaal" fill="#080c55" radius={[4, 4, 0, 0]} />
                            <Bar dataKey="equity" name="Equity" fill="#00a4d4" radius={[4, 4, 0, 0]} />
                            <Bar dataKey="financed" name="Gefinancierd" fill="#28a745" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardHeader className="py-3">
                        <CardTitle className="text-sm font-medium">Trucks & Vermogen per Stage</CardTitle>
                      </CardHeader>
                      <CardContent className="h-72">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                            <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                            <Tooltip />
                            <Legend />
                            <Bar yAxisId="left" dataKey="trucks" name="Trucks" fill="#ff9100" radius={[4, 4, 0, 0]} />
                            <Bar yAxisId="right" dataKey="power" name="Vermogen (kW)" fill="#28a745" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>
              </Tabs>
              
              {/* Footer */}
              <div className="flex items-center justify-between pt-3 border-t text-xs text-muted-foreground">
                <span>Data van {new Date().toLocaleDateString('nl-NL')}</span>
                <span>GreenVision Charging © {new Date().getFullYear()}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Missing Drawings Alert for Bulk Export */}
      <AlertDialog open={showBulkMissingDrawingAlert} onOpenChange={setShowBulkMissingDrawingAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
              ⚠️ Locaties zonder tekening
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                De volgende {missingDrawingLocations.length} locatie(s) hebben geen tekening gekoppeld:
              </p>
              <ul className="bg-muted p-3 rounded-lg text-sm space-y-1 max-h-40 overflow-auto">
                {missingDrawingLocations.slice(0, 5).map((loc) => (
                  <li key={loc.id} className="flex items-center gap-2">
                    <span className="font-mono text-xs text-muted-foreground">{loc.locode || '—'}</span>
                    <span>{loc.name}</span>
                  </li>
                ))}
                {missingDrawingLocations.length > 5 && (
                  <li className="text-muted-foreground italic">
                    + {missingDrawingLocations.length - 5} meer...
                  </li>
                )}
              </ul>
              <p className="text-sm text-muted-foreground">
                Je kunt toch doorgaan, maar de slides worden dan zonder tekening afbeelding gegenereerd.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleer</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleBulkSlideExport(true)}>
              Negeer & Download
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
